/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, LoadScript, Marker, Polyline } from '@react-google-maps/api';
import userPermissionContext from '../../context/userPermissionContext';

const STOP_SPEED_THRESHOLD = 3;

const parseDurationToSec = (text = '') => {
  if (!text || typeof text !== 'string') return 0;
  const h = Number((text.match(/(\d+)\s*h/) || [])[1] || 0);
  const m = Number((text.match(/(\d+)\s*min/) || [])[1] || 0);
  const s = Number((text.match(/(\d+)\s*s/) || [])[1] || 0);
  return (h * 3600) + (m * 60) + s;
};

const formatDuration = (sec = 0) => {
  const safeSec = Math.max(0, Number(sec) || 0);
  const h = Math.floor(safeSec / 3600);
  const m = Math.floor((safeSec % 3600) / 60);
  const s = safeSec % 60;
  if (h > 0) return `${h} hr ${m} min`;
  if (m > 0) return `${m} min ${s} sec`;
  return `${s} sec`;
};

const statusLabel = (code) => (code === 'MOVE' ? 'Moving' : 'Stopped');

const toNum = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const addressCache = new Map();
const geocodeQueue = [];
let geocodeBusy = false;
const GEOCODE_THROTTLE_MS = 180;
const GEOCODE_MAX_RETRY = 5;
const MAX_STOP_MARKERS = 25;
const MAX_ROUTE_SAMPLE_POINTS = 25;

function enqueueGeocode(lat, lng) {
  const key = `${Number(lat).toFixed(4)},${Number(lng).toFixed(4)}`;
  if (addressCache.has(key)) {
    return Promise.resolve(addressCache.get(key));
  }
  return new Promise((resolve) => {
    geocodeQueue.push({ lat, lng, key, resolve, retryCount: 0 });
    processGeocodeQueue();
  });
}

function processGeocodeQueue() {
  if (geocodeBusy || geocodeQueue.length === 0) return;
  geocodeBusy = true;
  const { lat, lng, key, resolve, retryCount = 0 } = geocodeQueue.shift();

  if (addressCache.has(key)) {
    resolve(addressCache.get(key));
    geocodeBusy = false;
    setTimeout(processGeocodeQueue, 0);
    return;
  }

  const done = (text) => {
    addressCache.set(key, text);
    resolve(text);
    geocodeBusy = false;
    setTimeout(processGeocodeQueue, GEOCODE_THROTTLE_MS);
  };

  if (typeof window === 'undefined' || !window.google?.maps?.Geocoder) {
    done(`${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`);
    return;
  }

  const geocoder = new window.google.maps.Geocoder();
  geocoder.geocode({ location: { lat: Number(lat), lng: Number(lng) } }, (results, status) => {
    if (status === 'OVER_QUERY_LIMIT' && retryCount < GEOCODE_MAX_RETRY) {
      geocodeBusy = false;
      geocodeQueue.unshift({ lat, lng, key, resolve, retryCount: retryCount + 1 });
      setTimeout(processGeocodeQueue, GEOCODE_THROTTLE_MS * (retryCount + 2));
      return;
    }

    const text =
      status === 'OK' && results?.[0]?.formatted_address
        ? results[0].formatted_address
        : `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
    done(text);
  });
}

function ReverseAddress({ lat, lng, apiAddress = '', mapsReady }) {
  const [text, setText] = useState(() => (apiAddress || '…'));

  useEffect(() => {
    if (apiAddress) {
      setText(apiAddress);
      return;
    }
    if (lat == null || lng == null) {
      setText('—');
      return;
    }

    const latLngFallback = `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`;
    const geocoderAvailable = typeof window !== 'undefined' && !!window.google?.maps?.Geocoder;
    if (!mapsReady && !geocoderAvailable) {
      setText(latLngFallback);
      return;
    }
    setText('…');
    let cancelled = false;
    enqueueGeocode(lat, lng).then((addr) => {
      if (!cancelled) setText(addr);
    });
    return () => {
      cancelled = true;
    };
  }, [lat, lng, apiAddress, mapsReady]);

  return <span className='small'>{text}</span>;
}

const normalizeVehicleData = (apiRows = []) => {
  const vehicles = Array.isArray(apiRows) ? apiRows : [];
  const firstVehicle = vehicles[0] || {};
  const rawPoints = [];

  vehicles.forEach((vehicle, vehicleIndex) => {
    const historyBlocks = Array.isArray(vehicle?.history) ? vehicle.history : [];

    historyBlocks.forEach((block, blockIndex) => {
      const blockDistance = toNum(block?.distance, 0);
      const items = Array.isArray(block?.items) ? block.items : [];

      items.forEach((item, itemIndex) => {
        const lat = Number(item?.latitude ?? item?.lat);
        const lng = Number(item?.longitude ?? item?.lng);
        const speed = toNum(item?.speed, 0);
        const time = item?.device_time || item?.datetime || item?.created_at || block?.raw_time || '';

        if (!time || Number.isNaN(lat) || Number.isNaN(lng)) return;

        const apiAddress = String(item?.address || item?.location || '').trim();

        rawPoints.push({
          id: `${vehicleIndex}-${blockIndex}-${itemIndex}`,
          time,
          lat,
          lng,
          speed,
          deltaDistanceKm: toNum(item?.distance, 0),
          blockDistanceKm: blockDistance,
          eventType: speed > STOP_SPEED_THRESHOLD ? 'MOVE' : 'STOP',
          apiAddress,
        });
      });
    });
  });

  rawPoints.sort((a, b) => new Date(a.time) - new Date(b.time));

  const seen = new Set();
  const points = [];
  rawPoints.forEach((p) => {
    const key = `${p.time}|${p.lat}|${p.lng}|${p.speed}`;
    if (seen.has(key)) return;
    seen.add(key);
    points.push(p);
  });

  const events = [];
  let current = null;
  points.forEach((p, pointIndex) => {
    const type = p.speed > STOP_SPEED_THRESHOLD ? 'MOVE' : 'STOP';
    if (!current || current.type !== type) {
      if (current) events.push(current);
      current = {
        type,
        startTime: p.time,
        endTime: p.time,
        distanceKm: 0,
        topSpeedKph: p.speed,
        pointCount: 1,
        startLat: p.lat,
        startLng: p.lng,
        endLat: p.lat,
        endLng: p.lng,
        startPointIndex: pointIndex,
        endPointIndex: pointIndex,
      };
      return;
    }

    current.endTime = p.time;
    current.distanceKm += p.deltaDistanceKm;
    current.topSpeedKph = Math.max(current.topSpeedKph, p.speed);
    current.pointCount += 1;
    current.endLat = p.lat;
    current.endLng = p.lng;
    current.endPointIndex = pointIndex;
  });
  if (current) events.push(current);

  const totalDistanceByPoints = points.reduce((sum, p) => sum + p.deltaDistanceKm, 0);
  const moveDurationSec = parseDurationToSec(firstVehicle?.move_duration);
  const stopDurationSec = parseDurationToSec(firstVehicle?.stop_duration);
  const topSpeedFromApi = toNum(String(firstVehicle?.top_speed || '').replace(/[^0-9.]/g, ''), 0);
  const topSpeedFromPoints = points.reduce((max, p) => Math.max(max, p.speed), 0);
  const topSpeedKph = Math.max(topSpeedFromApi, topSpeedFromPoints);
  const totalDurationSec = moveDurationSec + stopDurationSec;
  const avgSpeedKph = totalDurationSec > 0 ? (totalDistanceByPoints / (totalDurationSec / 3600)) : 0;

  return {
    vehicle: {
      name: firstVehicle?.name || '-',
      totalDistanceApi: firstVehicle?.distance_sum || '-',
    },
    summary: {
      totalDistanceKm: totalDistanceByPoints,
      topSpeedKph,
      avgSpeedKph,
      moveDurationSec,
      stopDurationSec,
      totalPoints: rawPoints.length,
      validPoints: points.length,
      duplicatePointsRemoved: rawPoints.length - points.length,
      firstPointTime: points[0]?.time || '-',
      lastPointTime: points[points.length - 1]?.time || '-',
    },
    points,
    events,
  };
};

const sampleRoutePoints = (path = [], maxPoints = MAX_ROUTE_SAMPLE_POINTS) => {
  if (!Array.isArray(path) || path.length <= 2) return path;
  const step = Math.max(1, Math.ceil(path.length / maxPoints));
  const sampled = path.filter((_, index) => index % step === 0);
  const first = path[0];
  const last = path[path.length - 1];
  if (sampled[0] !== first) sampled.unshift(first);
  if (sampled[sampled.length - 1] !== last) sampled.push(last);
  return sampled.slice(0, maxPoints - 1).concat(last);
};

function VehicleHistoryData({ defaultDeviceName = '' }) {
  const { jwt } = useContext(userPermissionContext);
  const mapRef = useRef(null);

  const getToday = () => new Date().toISOString().slice(0, 10);

  const [filters, setFilters] = useState({
    device_name: defaultDeviceName || '',
    from_date: getToday(),
    to_date: getToday(),
    from_time: '00:00:00',
    to_time: '23:59:59',
  });
  const [rawHistory, setRawHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [mapsReady, setMapsReady] = useState(false);
  const [selectedTripIndex, setSelectedTripIndex] = useState(null);
  const [activeViewTab, setActiveViewTab] = useState('tripMap');
  const [snappedFullPath, setSnappedFullPath] = useState([]);
  const [snappedSelectedTripPath, setSnappedSelectedTripPath] = useState([]);

  useEffect(() => {
    if (!filters.device_name && defaultDeviceName) {
      setFilters((prev) => ({ ...prev, device_name: defaultDeviceName }));
    }
  }, [defaultDeviceName]);

  // Auto-fire the history fetch once when we land on this page with a
  // device name pre-filled (e.g. opened from "Trip history" on another page).
  // Guarded by a ref so we don't refetch on every filter edit.
  const hasAutoFetchedRef = useRef(false);
  useEffect(() => {
    if (hasAutoFetchedRef.current) return;
    if (!defaultDeviceName) return;
    if (!filters.device_name) return;
    if (!jwt?.token) return;
    if (!localStorage.getItem('TrackerToken')) return;
    hasAutoFetchedRef.current = true;
    getHistoryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.device_name, jwt?.token, defaultDeviceName]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const getHistoryData = () => {
    const trackerToken = localStorage.getItem('TrackerToken');

    if (!trackerToken) {
      setError('Open the tracker login first, then try again.');
      return;
    }

    if (!filters.device_name) {
      setError('Enter the vehicle or device name.');
      return;
    }

    setIsLoading(true);
    setError('');

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + jwt.token,
      },
      body: JSON.stringify({
        ...filters,
        tracker_token: trackerToken,
      }),
    };

    fetch(process.env.REACT_APP_API_URL + 'getHistoryData', requestOptions)
      .then((resp) => resp.json())
      .then((resp) => {
        // Backend has returned multiple shapes over time; normalize them safely.
        let responseData = [];
        if (Array.isArray(resp)) {
          responseData = resp;
        } else if (Array.isArray(resp?.data)) {
          responseData = resp.data;
        } else if (resp?.data && typeof resp.data === 'object' && Array.isArray(resp.data?.history)) {
          responseData = [resp.data];
        } else if (resp && typeof resp === 'object' && Array.isArray(resp?.history)) {
          responseData = [resp];
        }

        if (!responseData.length) {
          const backendMessage = resp?.message || resp?.data || '';
          setError(
            typeof backendMessage === 'string' && backendMessage.trim()
              ? backendMessage
              : 'No route data found for this vehicle and time range.'
          );
        }

        setRawHistory(responseData);
        setSelectedTripIndex(null);
        setSelectedPoint(null);
      })
      .catch((err) => {
        setError(err?.message || 'Failed to load history data');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const normalizedData = useMemo(() => normalizeVehicleData(rawHistory), [rawHistory]);

  const mapPoints = useMemo(() => {
    return normalizedData.points.map((item, index) => ({
      ...item,
      id: `${item.lat}-${item.lng}-${index}`,
    }));
  }, [normalizedData.points]);

  const mapCenter = useMemo(() => {
    if (mapPoints.length > 0) {
      return { lat: mapPoints[0].lat, lng: mapPoints[0].lng };
    }
    return { lat: 23.7806, lng: 90.4070 };
  }, [mapPoints]);

  const polylinePath = useMemo(() => mapPoints.map((item) => ({ lat: item.lat, lng: item.lng })), [mapPoints]);
  const startPoint = mapPoints[0] || null;
  const endPoint = mapPoints.length > 1 ? mapPoints[mapPoints.length - 1] : null;
  const stopMarkers = useMemo(() => {
    return normalizedData.events
      .filter((event) => event.type === 'STOP')
      .slice(0, MAX_STOP_MARKERS)
      .map((event, index) => ({
        id: `stop-${index}-${event.startTime}`,
        lat: event.startLat,
        lng: event.startLng,
        time: event.startTime,
        eventType: 'STOP',
        speed: 0,
        apiAddress: '',
      }));
  }, [normalizedData.events]);
  const selectedTrip = selectedTripIndex != null ? normalizedData.events[selectedTripIndex] : null;
  const showOnlySelectedTripMarkers = selectedTripIndex != null;
  const selectedTripPolylinePath = useMemo(() => {
    if (!selectedTrip || selectedTrip.startPointIndex == null || selectedTrip.endPointIndex == null) return [];
    return mapPoints
      .slice(selectedTrip.startPointIndex, selectedTrip.endPointIndex + 1)
      .map((item) => ({ lat: item.lat, lng: item.lng }));
  }, [mapPoints, selectedTrip]);
  const selectedTripMarkers = useMemo(() => {
    if (!selectedTrip) return [];
    return [
      {
        id: 'trip-start',
        label: 'S',
        title: 'Selected Trip Start',
        lat: selectedTrip.startLat,
        lng: selectedTrip.startLng,
        time: selectedTrip.startTime,
        eventType: selectedTrip.type,
        speed: selectedTrip.topSpeedKph || 0,
        apiAddress: '',
      },
      {
        id: 'trip-end',
        label: 'E',
        title: 'Selected Trip End',
        lat: selectedTrip.endLat,
        lng: selectedTrip.endLng,
        time: selectedTrip.endTime,
        eventType: selectedTrip.type,
        speed: selectedTrip.topSpeedKph || 0,
        apiAddress: '',
      },
    ];
  }, [selectedTrip]);

  useEffect(() => {
    if (!mapsReady || !window.google?.maps?.DirectionsService || polylinePath.length < 2) {
      setSnappedFullPath([]);
      return;
    }

    const sampled = sampleRoutePoints(polylinePath);
    if (sampled.length < 2) {
      setSnappedFullPath([]);
      return;
    }

    let cancelled = false;
    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin: sampled[0],
        destination: sampled[sampled.length - 1],
        waypoints: sampled.slice(1, -1).map((point) => ({ location: point, stopover: false })),
        optimizeWaypoints: false,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (cancelled) return;
        if (status === 'OK' && result?.routes?.[0]?.overview_path?.length > 1) {
          const snapped = result.routes[0].overview_path.map((p) => ({ lat: p.lat(), lng: p.lng() }));
          setSnappedFullPath(snapped);
          return;
        }
        setSnappedFullPath([]);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [mapsReady, polylinePath]);

  useEffect(() => {
    if (!mapsReady || !window.google?.maps?.DirectionsService || selectedTripPolylinePath.length < 2) {
      setSnappedSelectedTripPath([]);
      return;
    }

    const sampled = sampleRoutePoints(selectedTripPolylinePath);
    if (sampled.length < 2) {
      setSnappedSelectedTripPath([]);
      return;
    }

    let cancelled = false;
    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin: sampled[0],
        destination: sampled[sampled.length - 1],
        waypoints: sampled.slice(1, -1).map((point) => ({ location: point, stopover: false })),
        optimizeWaypoints: false,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (cancelled) return;
        if (status === 'OK' && result?.routes?.[0]?.overview_path?.length > 1) {
          const snapped = result.routes[0].overview_path.map((p) => ({ lat: p.lat(), lng: p.lng() }));
          setSnappedSelectedTripPath(snapped);
          return;
        }
        setSnappedSelectedTripPath([]);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [mapsReady, selectedTripPolylinePath]);

  const handleMapLoad = (mapInstance) => {
    mapRef.current = mapInstance;
    if (!polylinePath.length || !window.google?.maps?.LatLngBounds) return;

    const bounds = new window.google.maps.LatLngBounds();
    polylinePath.forEach((p) => bounds.extend(p));
    mapInstance.fitBounds(bounds);
  };
  useEffect(() => {
    if (!mapRef.current || !selectedTrip || !window.google?.maps?.LatLngBounds) return;
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat: selectedTrip.startLat, lng: selectedTrip.startLng });
    bounds.extend({ lat: selectedTrip.endLat, lng: selectedTrip.endLng });
    mapRef.current.fitBounds(bounds);
    setSelectedPoint({
      lat: selectedTrip.startLat,
      lng: selectedTrip.startLng,
      time: selectedTrip.startTime,
      eventType: selectedTrip.type,
      speed: selectedTrip.topSpeedKph || 0,
      apiAddress: '',
    });
  }, [selectedTrip]);

  const pointCount = normalizedData.points.length;

  return (
    <div>
      <div className='row'>
        <div className='col-md-2 mb-3'>
          <label className='form-label'>Vehicle</label>
          <input
            type='text'
            className='form-control form-control-sm'
            name='device_name'
            value={filters.device_name}
            onChange={handleFilterChange}
            placeholder='Number plate or device ID'
          />
        </div>
        <div className='col-md-2 mb-3'>
          <label className='form-label'>From (date)</label>
          <input
            type='date'
            className='form-control form-control-sm'
            name='from_date'
            value={filters.from_date}
            onChange={handleFilterChange}
          />
        </div>
        <div className='col-md-2 mb-3'>
          <label className='form-label'>From (time)</label>
          <input
            type='time'
            className='form-control form-control-sm'
            name='from_time'
            step='1'
            value={filters.from_time}
            onChange={handleFilterChange}
          />
        </div>
        <div className='col-md-2 mb-3'>
          <label className='form-label'>To (date)</label>
          <input
            type='date'
            className='form-control form-control-sm'
            name='to_date'
            value={filters.to_date}
            onChange={handleFilterChange}
          />
        </div>
        <div className='col-md-2 mb-3'>
          <label className='form-label'>To (time)</label>
          <input
            type='time'
            className='form-control form-control-sm'
            name='to_time'
            step='1'
            value={filters.to_time}
            onChange={handleFilterChange}
          />
        </div>
        <div className='col-md-2 mb-3 d-flex align-items-end'>
          <button
            type='button'
            className='btn btn-primary btn-sm w-100'
            onClick={getHistoryData}
            disabled={isLoading}
          >
            {isLoading ? 'Loading…' : 'Load route'}
          </button>
        </div>
      </div>

      {error && (
        <div className='alert alert-danger py-2' role='alert'>
          {error}
        </div>
      )}

      {!error && !isLoading && normalizedData.points.length === 0 && (
        <div className='alert alert-warning py-2' role='alert'>
          No route data found for this vehicle and time range.
        </div>
      )}

      <div className='row g-2 mb-2 mt-2'>
        <div className='col-6 col-md-4 col-lg-2'>
          <div className='border rounded p-3 bg-light h-100'>
            <div className='small text-uppercase text-muted fw-bold'>Vehicle</div>
            <div className='fs-4 fw-bolder text-primary'>{normalizedData.vehicle.name}</div>
          </div>
        </div>
        <div className='col-6 col-md-4 col-lg-2'>
          <div className='border rounded p-3 bg-light h-100'>
            <div className='small text-uppercase text-muted fw-bold'>Total distance</div>
            <div className='fs-4 fw-bolder'>{normalizedData.summary.totalDistanceKm.toFixed(1)} km</div>
          </div>
        </div>
        <div className='col-6 col-md-4 col-lg-2'>
          <div className='border rounded p-3 bg-light h-100'>
            <div className='small text-uppercase text-muted fw-bold'>Fastest speed</div>
            <div className='fs-4 fw-bolder text-danger'>{normalizedData.summary.topSpeedKph} km/h</div>
          </div>
        </div>
        <div className='col-6 col-md-4 col-lg-2'>
          <div className='border rounded p-3 bg-light h-100'>
            <div className='small text-uppercase text-muted fw-bold'>Average speed</div>
            <div className='fs-4 fw-bolder'>{normalizedData.summary.avgSpeedKph.toFixed(0)} km/h</div>
          </div>
        </div>
        <div className='col-6 col-md-4 col-lg-2'>
          <div className='border rounded p-3 bg-light h-100'>
            <div className='small text-uppercase text-muted fw-bold'>Time driving</div>
            <div className='fs-4 fw-bolder text-success'>{formatDuration(normalizedData.summary.moveDurationSec)}</div>
          </div>
        </div>
        <div className='col-6 col-md-4 col-lg-2'>
          <div className='border rounded p-3 bg-light h-100'>
            <div className='small text-uppercase text-muted fw-bold'>Time stopped</div>
            <div className='fs-4 fw-bolder text-secondary'>{formatDuration(normalizedData.summary.stopDurationSec)}</div>
          </div>
        </div>
      </div>

      <div className='border rounded bg-light px-3 py-2 mb-3 small'>
        <span className='fw-bold'>First</span> → <span>Last GPS time:</span>{' '}
        {normalizedData.summary.firstPointTime} - {normalizedData.summary.lastPointTime}
        <span className='mx-2'>|</span>
        <span className='fw-bold'>{pointCount}</span> GPS points
      </div>

      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_MAP_API_KEY}
        onLoad={() => setMapsReady(true)}
      >
        <div className='row g-3 mb-3'>
          <div className='col-lg-7'>
            <div className='border rounded bg-white h-100'>
              <div className='d-flex justify-content-between align-items-center border-bottom px-3 py-2'>
                <div>
                  <div className='fw-bold'>Route map</div>
                  <div className='small text-muted'>
                    <span className='text-primary fw-bold'>Blue</span> = full route,{' '}
                    <span className='text-success fw-bold'>S</span> = start,{' '}
                    <span className='text-danger fw-bold'>E</span> = end,{' '}
                    <span className='text-warning fw-bold'>P</span> = stop
                  </div>
                </div>
              </div>

              {selectedTrip && (
                <div className='alert alert-info py-2 mb-2 rounded-0 border-0'>
                  Selected trip distance:{' '}
                  <strong>{selectedTrip.distanceKm >= 0.01 ? `${selectedTrip.distanceKm.toFixed(2)} km` : '0.00 km'}</strong>
                  {' '}({statusLabel(selectedTrip.type)})
                </div>
              )}

              <div className='px-2 pb-2'>
                <GoogleMap
                  zoom={11}
                  mapContainerStyle={{ height: '420px', width: '100%' }}
                  center={mapCenter}
                  onLoad={handleMapLoad}
                >
                  {!showOnlySelectedTripMarkers && (snappedFullPath.length > 1 || polylinePath.length > 1) && (
                    <Polyline
                      path={snappedFullPath.length > 1 ? snappedFullPath : polylinePath}
                      options={{
                        strokeColor: '#0d6efd',
                        strokeOpacity: 0.9,
                        strokeWeight: 4,
                      }}
                    />
                  )}
                  {showOnlySelectedTripMarkers && (snappedSelectedTripPath.length > 1 || selectedTripPolylinePath.length > 1) && (
                    <Polyline
                      path={snappedSelectedTripPath.length > 1 ? snappedSelectedTripPath : selectedTripPolylinePath}
                      options={{
                        strokeColor: '#198754',
                        strokeOpacity: 1,
                        strokeWeight: 5,
                      }}
                    />
                  )}

                  {!showOnlySelectedTripMarkers && startPoint && (
                    <Marker
                      key='marker-start'
                      position={{ lat: startPoint.lat, lng: startPoint.lng }}
                      label='S'
                      onClick={() => setSelectedPoint(startPoint)}
                    />
                  )}

                  {!showOnlySelectedTripMarkers && endPoint && (
                    <Marker
                      key='marker-end'
                      position={{ lat: endPoint.lat, lng: endPoint.lng }}
                      label='E'
                      onClick={() => setSelectedPoint(endPoint)}
                    />
                  )}

                  {!showOnlySelectedTripMarkers && stopMarkers.map((point) => (
                    <Marker
                      key={point.id}
                      position={{ lat: point.lat, lng: point.lng }}
                      label='P'
                      onClick={() => setSelectedPoint(point)}
                    />
                  ))}
                  {selectedTripMarkers.map((point) => (
                    <Marker
                      key={point.id}
                      position={{ lat: point.lat, lng: point.lng }}
                      label={point.label}
                      title={point.title}
                      onClick={() => setSelectedPoint(point)}
                    />
                  ))}

                  {selectedPoint && (
                    <InfoWindow
                      position={{ lat: selectedPoint.lat, lng: selectedPoint.lng }}
                      onCloseClick={() => setSelectedPoint(null)}
                    >
                      <div style={{ maxWidth: 320 }}>
                        <div><strong>Status:</strong> {statusLabel(selectedPoint.eventType)}</div>
                        <div><strong>Time:</strong> {selectedPoint.time || '—'}</div>
                        <div><strong>Speed:</strong> {selectedPoint.speed != null ? `${selectedPoint.speed} km/h` : '—'}</div>
                        <div><strong>Place:</strong>{' '}
                          <ReverseAddress
                            lat={selectedPoint.lat}
                            lng={selectedPoint.lng}
                            apiAddress={selectedPoint.apiAddress}
                            mapsReady={mapsReady}
                          />
                        </div>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              </div>
            </div>
          </div>

          <div className='col-lg-5'>
            <div className='border rounded bg-white h-100 d-flex flex-column'>
              <div className='border-bottom px-3 py-2'>
                <div className='fw-bold'>Trip in parts</div>
                <div className='small text-muted'>Each row is one stretch of driving or stopping, in time order.</div>
              </div>
              <div className='px-2 pt-2'>
                <ul className='nav nav-pills nav-pills-sm mb-2'>
                  <li className='nav-item'>
                    <button
                      type='button'
                      className={`nav-link py-1 px-2 ${activeViewTab === 'tripMap' ? 'active' : ''}`}
                      onClick={() => setActiveViewTab('tripMap')}
                    >
                      Trip & Map
                    </button>
                  </li>
                  <li className='nav-item'>
                    <button
                      type='button'
                      className={`nav-link py-1 px-2 ${activeViewTab === 'gpsPoints' ? 'active' : ''}`}
                      onClick={() => setActiveViewTab('gpsPoints')}
                    >
                      All GPS points ({pointCount})
                    </button>
                  </li>
                </ul>
              </div>
              <div className='table-responsive' style={{ maxHeight: '480px', overflowY: 'auto' }}>
                {activeViewTab === 'tripMap' ? (
                  <table className='table table-bordered table-sm align-middle mb-0'>
                    <thead className='table-light'>
                      <tr>
                        <th>#</th>
                        <th>Type</th>
                        <th>Start</th>
                        <th>End</th>
                        <th>Distance</th>
                        <th>Speed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {normalizedData.events.length > 0 ? (
                        normalizedData.events.map((event, index) => (
                          <tr
                            key={`${event.startTime}-${event.type}-${index}`}
                            style={{ cursor: 'pointer' }}
                            className={selectedTripIndex === index ? 'table-active' : ''}
                            onClick={() => setSelectedTripIndex(selectedTripIndex === index ? null : index)}
                          >
                            <td>{index + 1}</td>
                            <td>
                              <span
                                className={`badge fw-bolder ${
                                  event.type === 'MOVE' ? 'badge-light-success' : 'badge-light-danger'
                                }`}
                              >
                                {statusLabel(event.type)}
                              </span>
                            </td>
                            <td className='text-nowrap'>{event.startTime}</td>
                            <td className='text-nowrap'>{event.endTime}</td>
                            <td>{event.distanceKm >= 0.01 ? `${event.distanceKm.toFixed(2)} km` : '—'}</td>
                            <td>{event.type === 'MOVE' ? `${event.topSpeedKph} km/h` : '—'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan='6' className='text-center text-muted py-3'>Load a route to see trip parts.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                ) : (
                  <table className='table table-striped table-bordered table-sm align-middle mb-0'>
                    <thead className='table-light'>
                      <tr>
                        <th>#</th>
                        <th>When</th>
                        <th>Where</th>
                        <th>Speed</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {normalizedData.points.length > 0 ? (
                        normalizedData.points.map((item, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td className='text-nowrap'>{item.time}</td>
                            <td>
                              <ReverseAddress
                                lat={item.lat}
                                lng={item.lng}
                                apiAddress={item.apiAddress}
                                mapsReady={mapsReady}
                              />
                            </td>
                            <td>{item.speed != null ? `${item.speed} km/h` : '—'}</td>
                            <td>
                              <span
                                className={`badge fw-bolder ${
                                  item.eventType === 'MOVE' ? 'badge-light-success' : 'badge-light-danger'
                                }`}
                              >
                                {statusLabel(item.eventType)}
                              </span>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan='5' className='text-center text-muted py-3'>
                            No points yet. Load a route first.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </LoadScript>
    </div>
  );
}

export default VehicleHistoryData;
